/**
 * One socket, many charts.
 *
 * ## The defect this replaces
 *
 * `App.tsx` built a `Feed` inside an effect keyed on `symbol`, with the
 * message handlers closing over that symbol to filter with. Its comment said
 * "the feed is created once" and the dependency array said otherwise, so every
 * symbol change tore down a WebSocket and opened a new one. A second effect
 * calling `setSymbols` — the mechanism that exists precisely so the socket does
 * NOT have to be replaced — ran immediately afterwards on the fresh socket,
 * doing nothing the reconnect had not already done.
 *
 * That was survivable with one chart. It is not survivable with a workspace of
 * them: the filter `if (m.s !== symbol) return` has no answer to "which of the
 * four panes wanted this", and one socket per pane is both wasteful and, under
 * the licensing constraints in CLAUDE.md, the wrong shape — a user's devices
 * are fed from a single upstream session, and multiplying browser sockets works
 * against the grain of that.
 *
 * ## What this is
 *
 * A registry between the `Feed` and whatever is drawing. Panes subscribe by
 * symbol; the router keeps the union, tells the caller when it changes, and
 * routes each message to the subscribers that asked for that symbol. It holds
 * no socket and performs no I/O — the `Feed` stays where it is and calls
 * `deliver`, which is what makes every rule below testable without a server.
 */
import type {
  AnnMessage, BarMessage, ServerMessage, StatusMessage, TickMessage,
} from './ws.js';

export interface SymbolHandlers {
  readonly onTick?: (m: TickMessage) => void;
  readonly onBar?: (m: BarMessage) => void;
  readonly onAnalysis?: (m: AnnMessage) => void;
  readonly onStatus?: (m: StatusMessage) => void;
}

export interface FeedRouterOptions {
  /**
   * Called when, and only when, the set of subscribed symbols changes.
   *
   * The caller forwards this to `Feed.setSymbols`. It is deliberately not
   * called on a subscribe to a symbol somebody else already holds: the
   * subscription is already in place, and every message the fan-out evaluates
   * runs an entitlement check, so a redundant round trip is a real cost paid on
   * the server for nothing.
   */
  readonly onSymbolsChanged?: (symbols: readonly string[]) => void;
  /**
   * A subscriber's handler threw.
   *
   * Delivery continues to everyone else. One pane with a bug must not freeze
   * the other three — but a swallowed exception is a pane that looks frozen for
   * no visible reason, so it is handed out rather than dropped.
   */
  readonly onHandlerError?: (error: unknown, symbol: string) => void;
}

/** A live subscription. Calling it twice is safe and does nothing the second time. */
export type Unsubscribe = () => void;

export class FeedRouter {
  /**
   * Symbol -> its subscribers, in subscription order.
   *
   * A Set rather than a count, because the identity of each subscriber is what
   * makes unsubscribe idempotent. With a bare refcount, a double-invoked
   * cleanup — React's StrictMode does exactly this in development — would
   * decrement twice and silently unsubscribe a pane that is still on screen.
   *
   * `Map` and `Set` both iterate in insertion order, which is what gives
   * `symbols()` and delivery a defined sequence instead of an implementation
   * detail that tests would have to guess at.
   */
  private readonly subscribers = new Map<string, Set<SymbolHandlers>>();

  constructor(private readonly opts: FeedRouterOptions = {}) {}

  subscribe(symbol: string, handlers: SymbolHandlers): Unsubscribe {
    // The empty string is not a symbol. `App.tsx` holds it for the render
    // between mount and the boot effect resolving a default, and subscribing to
    // it asks the fan-out to entitlement-check a symbol that cannot exist.
    if (!symbol) return () => {};

    let existing = this.subscribers.get(symbol);
    const isNew = existing === undefined;
    if (!existing) {
      existing = new Set();
      this.subscribers.set(symbol, existing);
    }
    existing.add(handlers);
    if (isNew) this.announce();

    let done = false;
    return () => {
      if (done) return;
      done = true;
      const held = this.subscribers.get(symbol);
      if (!held) return;
      held.delete(handlers);
      if (held.size === 0) {
        this.subscribers.delete(symbol);
        this.announce();
      }
    };
  }

  /** The symbols with at least one subscriber, in the order they were first taken. */
  symbols(): readonly string[] {
    return [...this.subscribers.keys()];
  }

  /**
   * Route one server message.
   *
   * `hello` and `error` are connection-level — they describe the socket, not a
   * symbol — so they are not routed here and stay with whoever owns the `Feed`.
   */
  deliver(m: ServerMessage): void {
    switch (m.t) {
      case 'tick': this.fan(m.s, (h) => h.onTick?.(m)); break;
      case 'bar': this.fan(m.s, (h) => h.onBar?.(m)); break;
      case 'ann': this.fan(m.s, (h) => h.onAnalysis?.(m)); break;
      case 'status': this.fan(m.s, (h) => h.onStatus?.(m)); break;
      default: break;
    }
  }

  private fan(symbol: string, call: (h: SymbolHandlers) => void): void {
    const held = this.subscribers.get(symbol);
    if (!held) return;
    // A copy, because a handler is allowed to unsubscribe — a pane closing on
    // the tick that filled its last order is an ordinary thing to do, and
    // mutating a Set while iterating it skips whoever came next.
    for (const handlers of [...held]) {
      try {
        call(handlers);
      } catch (error) {
        this.opts.onHandlerError?.(error, symbol);
      }
    }
  }

  private announce(): void {
    this.opts.onSymbolsChanged?.(this.symbols());
  }
}
